import type { ComponentType } from 'react'
import React from 'react'
import { withDependencies } from '@wix/thunderbolt-ioc'
import type { CompProps, Experiments, ViewerModel } from '@wix/thunderbolt-symbols'
import { ExperimentsSymbol, ViewerModelSym } from '@wix/thunderbolt-symbols'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

const isCsr = !!process.env.browser

let useControllerHook: (
	displayedId: string,
	compType: string,
	_compProps: CompProps,
	compId: string
) => {
	[x: string]: any
}
;(async () => {
	isCsr && (await window.externalsRegistry.react.loaded)
	useControllerHook = require('./hooks').useControllerHook
})()

// Merges two className strings without array allocation.
function mergeClassNames(a: string = '', b: string = ''): string {
	if (a && b) {
		return `${a} ${b}`
	}
	return a || b
}

function addMissingClassNames(className: string, classNamesToAdd: Array<string>): string {
	const existingClassNames = new Set(className.split(/\s+/).filter(Boolean))
	let result = className

	classNamesToAdd.forEach((classNameToAdd) => {
		if (!existingClassNames.has(classNameToAdd)) {
			result = result ? `${result} ${classNameToAdd}` : classNameToAdd
			existingClassNames.add(classNameToAdd)
		}
	})

	return result
}

export const RunControllersWrapper = withDependencies(
	[ExperimentsSymbol, ViewerModelSym],
	(experiments: Experiments, viewerModel: ViewerModel) => {
		const addIdAsClassName = isExperimentOpen(experiments, 'specs.thunderbolt.addIdAsClassName')
		const preserveWixSelectClass = isExperimentOpen(experiments, 'preserveWixSelectClass')

		return {
			wrapComponent: (Component: ComponentType<any>) => {
				const Wrapper = ({
					compProps: storeProps,
					...restProps
				}: {
					compProps: any
					compId: string
					compClassType: string
					id: string
				}) => {
					const { id: displayedId, compId, compClassType } = restProps
					const compProps = useControllerHook(displayedId, compClassType, storeProps, compId)

					const props = {
						...compProps,
						...restProps,
					} as { [x: string]: any }

					// Repeater items need both ids: css can target either the base comp id or the inflated displayed id.
					const classNameIds = addIdAsClassName
						? displayedId === compId
							? [displayedId]
							: [compId, displayedId]
						: [compId]

					// Builder bindings apply visibility and state classes through restProps.
					// Other sites keep legacy class handling unless preserveWixSelectClass is open.
					const existingClassName =
						viewerModel.isBuilderComponentModel || preserveWixSelectClass
							? mergeClassNames(compProps?.className, (restProps as any)?.className)
							: (compProps?.className ?? '')

					const className = addMissingClassNames(existingClassName, classNameIds)

					return <Component {...props} className={className} />
				}
				return Wrapper
			},
		}
	}
)
